import { Component, Suspense } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { QueryErrorResetBoundary } from "@tanstack/react-query";
import Spinner from "components/ui/Spinner";
import ApiErrorBanner from "components/ApiErrorBanner";
import { NotFoundPage } from "pages/Error/NotFoundPage";
import { QueryError } from "utils/queries";

type QueryBoundaryProps = {
  children: ReactNode;
  fallback?: ReactNode;
  // "page" when the content below is what the page is for, so a 404 from it means
  // the page has nothing to show. Anything else keeps the retryable banner.
  notFound?: "page" | "banner";
};

/**
 * The standard boundary for content fetched through suspense queries, and for
 * lazy-loaded chunks.
 *
 * Its fallback is a bare spinner, which places itself; pass `fallback` for anything
 * else, such as the full-page spinner at the route root.
 */
export default function QueryBoundary(props: QueryBoundaryProps) {
  const fallback = props.fallback ?? <Spinner placement="fill" />;

  return (
    <Suspense fallback={fallback}>
      <QueryErrorResetBoundary>
        {({ reset }) => (
          <QueryErrorBoundary onReset={reset} notFound={props.notFound}>
            {props.children}
          </QueryErrorBoundary>
        )}
      </QueryErrorResetBoundary>
    </Suspense>
  );
}

type QueryErrorBoundaryProps = {
  children: ReactNode;
  // Clears the failed queries so remounting the subtree refetches rather than
  // rethrowing the cached rejection.
  onReset?: () => void;
  notFound?: "page" | "banner";
};

type QueryErrorBoundaryState = {
  error: QueryError | null;
};

export class QueryErrorBoundary extends Component<QueryErrorBoundaryProps> {
  state: QueryErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown) {
    if (error instanceof QueryError) {
      return { error };
    }
    return null;
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("Error caught in QueryErrorBoundary:", error, info);
  }

  retry = () => {
    this.props.onReset?.();
    this.setState({ error: null });
  };

  render() {
    const error = this.state.error;

    if (error?.isNotFound && this.props.notFound === "page") {
      return <NotFoundPage />;
    }

    if (error) {
      return <ApiErrorBanner error={error.fetchError} onRetry={this.retry} />;
    }

    return this.props.children;
  }
}
